import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { FilterField } from '@/components/FilterButton'
import { StatusFilterButton, type StatusFilterValue } from '@/components/StatusFilter'
import { LayoutSelect } from '@/components/LayoutSelect'
import { LineItemSelect } from '@/components/LineItemSelect'
import type { CreativeListOptions } from '@/services/creatives/creativesApi'

export interface CreativeFilterState {
  objectState: StatusFilterValue
  /** Line item id, or '' for every line item. */
  lineItemId: string
  /** Layout id, or '' for every layout. */
  layoutId: string
  /** Only creatives attached to no line item. */
  orphan: boolean
}

export const DEFAULT_CREATIVE_FILTERS: CreativeFilterState = {
  objectState: 'ACTIVE',
  lineItemId: '',
  layoutId: '',
  orphan: false,
}

/** The filters as the creative API takes them, besides the status. */
export function creativeFetchFilters(
  filters: CreativeFilterState
): Pick<CreativeListOptions, 'lineItemId' | 'layoutId' | 'orphan'> {
  return { lineItemId: filters.lineItemId, layoutId: filters.layoutId, orphan: filters.orphan }
}

interface CreativeFiltersProps {
  filters: CreativeFilterState
  onChange: (filters: CreativeFilterState) => void
  /** What this list treats as unfiltered. */
  defaults?: CreativeFilterState
}

/** Status, line item and layout filters for a creative list, in one button. */
export function CreativeFilters({
  filters,
  onChange,
  defaults = DEFAULT_CREATIVE_FILTERS,
}: CreativeFiltersProps) {
  const { t } = useTranslation()
  const orphanId = useId()

  return (
    <StatusFilterButton
      value={filters.objectState}
      onChange={(objectState) => onChange({ ...filters, objectState })}
      defaultValue={defaults.objectState}
      extraActiveCount={
        (filters.lineItemId !== defaults.lineItemId ? 1 : 0) +
        (filters.layoutId !== defaults.layoutId ? 1 : 0) +
        (filters.orphan !== defaults.orphan ? 1 : 0)
      }
      onReset={() => onChange(defaults)}
    >
      {/* The API narrows by one line item or by layout, not both, so a line
          item clears the layout and the other way round. */}
      <FilterField label={t('creatives.lineItem')}>
        <LineItemSelect
          value={filters.lineItemId}
          onChange={(lineItemId) =>
            onChange({
              ...filters,
              lineItemId,
              layoutId: lineItemId ? '' : filters.layoutId,
              orphan: lineItemId ? false : filters.orphan,
            })
          }
          anyLabel={t('common.all')}
          disabled={filters.orphan}
          className="h-8 text-sm"
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id={orphanId}
            checked={filters.orphan}
            onCheckedChange={(checked) =>
              onChange({
                ...filters,
                orphan: checked === true,
                lineItemId: checked === true ? '' : filters.lineItemId,
              })
            }
          />
          <Label htmlFor={orphanId} className="text-sm font-normal">
            {t('creatives.orphanOnly')}
          </Label>
        </div>
      </FilterField>

      <FilterField label={t('creatives.layout')}>
        <LayoutSelect
          value={filters.layoutId}
          onChange={(layoutId) =>
            onChange({ ...filters, layoutId, lineItemId: layoutId ? '' : filters.lineItemId })
          }
          anyLabel={t('common.all')}
          className="h-8 text-sm"
        />
      </FilterField>
    </StatusFilterButton>
  )
}
